import React, { ReactNode } from 'react';
import { LuArrowLeft, LuExternalLink } from 'react-icons/lu';
import { Link } from 'react-router-dom';

import { ProviderId } from '../../entities/provider';
import { ProviderBadge } from '../ui/ProviderBadge';

interface TaskDetailShellProps {
  backTo: string;
  backLabel?: string;
  provider: ProviderId;
  titleKey?: string;
  externalUrl?: string;
  externalLabel?: string;
  headerActions?: ReactNode;
  // Optional, opt-in header content rendered as its own row below the back-button bar — a place
  // for context that isn't the task's own title/body (e.g. which project this belongs to) and a
  // quick-glance status/priority/due-date strip, so the page doesn't read as cut off above the fold.
  title?: ReactNode;
  meta?: ReactNode;
  sidebar?: ReactNode;
  isLoading?: boolean;
  error?: string | null;
  children: ReactNode;
}

// Shared chrome for a full-page task detail view — one consistent back-button/provider-badge/
// external-link/two-column layout across Jira, Trello, and Standalone, even though each provider's
// data source (and how much of it is even fetchable today) is completely different.
export const TaskDetailShell: React.FC<TaskDetailShellProps> = ({
  backTo,
  backLabel = 'Back',
  provider,
  titleKey,
  externalUrl,
  externalLabel,
  headerActions,
  title,
  meta,
  sidebar,
  isLoading,
  error,
  children,
}) => {
  if (isLoading) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center">
        <span className="loading loading-spinner loading-lg opacity-40" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-base-200 flex items-center justify-center p-6">
        <div className="alert alert-error max-w-sm flex-col items-start gap-2">
          <span>{error}</span>
          <Link to={backTo} className="btn btn-sm btn-ghost">
            {backLabel}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Link to={backTo} className="btn btn-ghost btn-sm btn-circle shrink-0" title={backLabel}>
              <LuArrowLeft size={16} />
            </Link>
            <ProviderBadge provider={provider} />
            {titleKey && <span className="font-mono text-xs opacity-40">{titleKey}</span>}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {headerActions}
            {externalUrl && (
              <a href={externalUrl} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm gap-1.5">
                {externalLabel ?? 'Open externally'}
                <LuExternalLink size={13} />
              </a>
            )}
          </div>
        </div>

        {(title || meta) && (
          <div className="space-y-1.5 pl-1">
            {title && <div className="text-lg font-semibold">{title}</div>}
            {meta && <div className="flex items-center gap-2 flex-wrap">{meta}</div>}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
          <div className="lg:col-span-2 space-y-4 min-w-0">{children}</div>
          {sidebar && <div className="space-y-4 min-w-0">{sidebar}</div>}
        </div>
      </div>
    </div>
  );
};

export default TaskDetailShell;
